/**
 * O casco dos painéis de gestão (Escola, Logigate): menu lateral branco que expande e
 * minimiza, cabeçalho com o trilho de onde se está, pesquisa ⌘K, notificações, tema e conta;
 * no telemóvel, gaveta à esquerda e barra de baixo.
 *
 * - **Expandir/minimizar:** botão no cabeçalho, no fundo do menu ou ⌘B. Fica guardado neste
 *   aparelho, por painel (`{chave}-recolhido`). Abaixo de 1024 px (tablet) o menu fica sempre
 *   só com os ícones e «Expandir» abre a gaveta.
 * - **A página desliza no documento** (não numa caixa), com a barra fina `scroll-bonito`.
 * - `semMargens` tira as margens do miolo (as apps de ecrã inteiro).
 *
 * Os menus vêm do servidor e convertem-se com `menuDoServidor` (ver `menu.ts`).
 */
import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import { Bell, ChevronRight, Menu as IconeMenu, PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, BotaoIcone } from "../base";
import { CorDoPortal, definirCorDoPortal } from "../cores";
import { Dica, ItemMenu, Menu, useSaida } from "../sobreposicoes";
import { useEcraLargo } from "../modelos";
import { achatarMenu, caminhoActivo, ItemPainel } from "./menu";
import { MenuLateral } from "./MenuLateral";
import { AccaoPaleta, PaletaComandos } from "./PaletaComandos";
import { BarraInferior, DestinoBarra } from "./BarraInferior";
import { BotaoTema, EscolhaDoTemaProps, itensDoTema, TemaSegmentado } from "./EscolhaDoTema";

export interface EstruturaPainelProps {
    /** Distingue os painéis no `localStorage` («escola», «logigate»). */
    chave: string;
    cor: CorDoPortal;
    /** O logótipo e o nome no topo do menu. */
    marca: { logotipo: ReactNode; nome: ReactNode; detalhe?: ReactNode; href?: string };
    itens: ItemPainel[];
    /** Antes da árvore do menu (o cartão da empresa com o seletor, por exemplo). */
    antesDoMenu?: ReactNode;
    utilizador?: { nome: string; email?: string | null; foto?: string | null } | null;
    menuUtilizador?: ItemMenu[];
    /** «Sair» no menu da conta: um endereço (página inteira) ou uma função. */
    sair?: { href?: string; onSair?: () => void } | null;
    /** Botões do cabeçalho antes das notificações (pesquisar alunos, ocorrências…). */
    accoesCabecalho?: ReactNode;
    notificacoes?: { href: string; total?: number | string | null } | null;
    /** Os destinos da barra de baixo do telemóvel (o «Menu» junta-se sozinho no fim). */
    barraInferior?: DestinoBarra[];
    accoesPaleta?: AccaoPaleta[];
    /** O modo claro/escuro (o `useTema` do kit): Claro, Escuro ou Sistema (segue o aparelho). */
    tema?: EscolhaDoTemaProps | null;
    semMargens?: boolean;
    /** Avisos por cima da página (plano por pagar, acesso a expirar…). */
    avisos?: ReactNode;
    children: ReactNode;
}

function lerRecolhido(chave: string): boolean {
    try {
        return localStorage.getItem(chave) === "1";
    } catch {
        return false;
    }
}

function editavel(alvo: EventTarget | null): boolean {
    const el = alvo as HTMLElement | null;
    return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
}

export function EstruturaPainel({
    chave,
    cor,
    marca,
    itens,
    antesDoMenu,
    utilizador,
    menuUtilizador = [],
    sair,
    accoesCabecalho,
    notificacoes,
    barraInferior,
    accoesPaleta,
    tema,
    semMargens = false,
    avisos,
    children,
}: EstruturaPainelProps) {
    const { url } = usePage();
    const chaveRecolhido = `painel-${chave}-recolhido`;
    const [recolhido, setRecolhido] = useState(() => lerRecolhido(chaveRecolhido));
    const [gaveta, setGaveta] = useState(false);
    const [paleta, setPaleta] = useState(false);
    const saidaGaveta = useSaida(gaveta);
    // Entre 768 e 1024 px o menu largo comia quase metade do ecrã e as páginas passavam da
    // largura: fica só a barra dos ícones, e «Expandir» abre a gaveta com o menu inteiro.
    const cabeMenuLargo = useEcraLargo(1024);
    const menuEstreito = recolhido || !cabeMenuLargo;

    // Antes de pintar, para não piscar o verde de omissão.
    useLayoutEffect(() => {
        definirCorDoPortal(cor);
    }, [cor]);

    const activos = useMemo(() => caminhoActivo(itens, url), [itens, url]);
    const entradas = useMemo(() => achatarMenu(itens), [itens]);
    const folha = activos[activos.length - 1];

    const alternarRecolhido = useCallback(() => {
        setRecolhido((v) => {
            try {
                localStorage.setItem(chaveRecolhido, v ? "0" : "1");
            } catch {
                // Sem localStorage o menu abre sempre largo.
            }
            return !v;
        });
    }, [chaveRecolhido]);

    const expandirOuMinimizar = useCallback(() => {
        if (cabeMenuLargo) {
            alternarRecolhido();
        } else {
            setGaveta((v) => !v);
        }
    }, [cabeMenuLargo, alternarRecolhido]);

    // A página inteira usa a scrollbar fina enquanto o painel está montado.
    useEffect(() => {
        document.documentElement.classList.add("scroll-bonito");
        return () => document.documentElement.classList.remove("scroll-bonito");
    }, []);

    useEffect(() => router.on("navigate", () => setGaveta(false)), []);

    // ⌘K abre a pesquisa; ⌘B expande/minimiza o menu (não dentro de um campo).
    useEffect(() => {
        const tecla = (e: KeyboardEvent) => {
            if (!(e.metaKey || e.ctrlKey) || e.altKey) {
                return;
            }
            const k = e.key.toLowerCase();
            if (k === "k") {
                e.preventDefault();
                setPaleta((v) => !v);
            } else if (k === "b" && !editavel(e.target)) {
                e.preventDefault();
                expandirOuMinimizar();
            }
        };
        document.addEventListener("keydown", tecla);
        return () => document.removeEventListener("keydown", tecla);
    }, [expandirOuMinimizar]);

    useEffect(() => {
        if (!gaveta) {
            return;
        }
        const tecla = (e: KeyboardEvent) => e.key === "Escape" && setGaveta(false);
        document.addEventListener("keydown", tecla);
        const html = document.documentElement;
        html.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", tecla);
            html.style.overflow = "";
        };
    }, [gaveta]);

    const fundoMenu = "border-r border-zinc-200 bg-white dark:border-white/10 dark:bg-zinc-900";
    const cabecaMarca = (estreito: boolean) => {
        const conteudo = (
            <>
                <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden">{marca.logotipo}</span>
                {!estreito && (
                    <span className="min-w-0">
                        <span className="block truncate text-[13px] font-semibold leading-4 tracking-tight text-zinc-900 dark:text-white">{marca.nome}</span>
                        {marca.detalhe && <span className="block truncate text-[11px] leading-4 text-zinc-500 dark:text-zinc-400">{marca.detalhe}</span>}
                    </span>
                )}
            </>
        );
        const classe = cn("flex min-w-0 items-center gap-2.5", estreito && "justify-center");
        return marca.href ? <Link href={marca.href} className={classe}>{conteudo}</Link> : <div className={classe}>{conteudo}</div>;
    };

    const menuConta: ItemMenu[] = [
        ...menuUtilizador,
        ...(sair ? [{
            rotulo: "Sair",
            icone: <Icon icon="mdi:logout" />,
            perigo: true,
            separadorAntes: menuUtilizador.length > 0,
            onSelect: () => {
                if (sair.onSair) {
                    sair.onSair();
                } else if (sair.href) {
                    window.location.assign(sair.href);
                }
            },
        }] : []),
    ];

    const destinos: DestinoBarra[] = [
        ...(barraInferior ?? []).slice(0, 4).map((d) => (d.abrePesquisa ? { ...d, onClick: () => setPaleta(true), activo: paleta } : d)),
        { chave: "menu", rotulo: "Menu", icone: "mdi:menu", onClick: () => setGaveta(true), activo: gaveta },
    ];

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100 print:bg-white">
            {/* Menu do computador */}
            <aside
                data-menu-painel
                data-tour="menu"
                data-recolhido={menuEstreito ? "1" : "0"}
                className={cn("fixed inset-y-0 left-0 z-40 hidden flex-col transition-[width] duration-200 md:flex print:hidden", fundoMenu, menuEstreito ? "w-[72px]" : "w-72")}
            >
                <div className={cn("flex h-16 shrink-0 items-center", menuEstreito ? "justify-center px-2" : "px-4")}>{cabecaMarca(menuEstreito)}</div>
                {!menuEstreito && antesDoMenu && <div className="shrink-0 px-3 pb-3" data-tour="seletor">{antesDoMenu}</div>}
                {/* Sem a pesquisa no topo do menu: procura-se no cabeçalho («Pesquisar…» / ⌘K), que já encontra as páginas do menu. */}
                <MenuLateral itens={itens} activos={activos} recolhido={menuEstreito} chave={`painel-${chave}`} nome="Menu principal" pesquisavel={false} />
                <div data-tour="minimizar" className={cn("shrink-0 border-t border-zinc-100 dark:border-white/10", menuEstreito ? "flex justify-center p-2" : "p-2")}>
                    {menuEstreito ? (
                        <Dica rotulo="Expandir o menu (⌘B)" lado="right">
                            <button type="button" onClick={expandirOuMinimizar} aria-label="Expandir o menu" className="flex size-11 items-center justify-center rounded-xl text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white">
                                <PanelLeftOpen className="size-5" />
                            </button>
                        </Dica>
                    ) : (
                        <button type="button" onClick={expandirOuMinimizar} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white">
                            <PanelLeftClose className="size-5 shrink-0" />
                            <span className="flex-1 text-left">Minimizar o menu</span>
                            <kbd className="rounded border border-zinc-200 px-1.5 text-[10px] text-zinc-400 dark:border-zinc-700">⌘B</kbd>
                        </button>
                    )}
                </div>
            </aside>

            <div className={cn("transition-[padding] duration-200 print:pl-0", menuEstreito ? "md:pl-[72px]" : "md:pl-72")}>
                {/* Cabeçalho */}
                <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-zinc-200 bg-white/85 px-2 backdrop-blur-md print:hidden dark:border-zinc-800 dark:bg-zinc-900/85 sm:gap-3 sm:px-4 lg:px-6">
                    <BotaoIcone rotulo="Abrir o menu" tamanho="lg" className="md:hidden" onClick={() => setGaveta(true)}>
                        <IconeMenu className="size-5" />
                    </BotaoIcone>
                    <BotaoIcone rotulo={menuEstreito ? "Expandir o menu (⌘B)" : "Minimizar o menu (⌘B)"} tamanho="lg" className="hidden md:inline-flex" onClick={expandirOuMinimizar}>
                        {menuEstreito ? <PanelLeftOpen className="size-5" /> : <PanelLeftClose className="size-5" />}
                    </BotaoIcone>

                    {/* No telemóvel o nome da página; no computador, o trilho */}
                    <div className="min-w-0 flex-1 md:hidden">
                        <p className="truncate text-[15px] font-semibold">{folha?.rotulo ?? marca.nome}</p>
                    </div>
                    <nav aria-label="Onde está" className="hidden min-w-0 flex-1 items-center gap-1.5 text-sm md:flex">
                        <span className="mr-1 h-5 w-px bg-zinc-200 dark:bg-zinc-700" aria-hidden />
                        {activos.length > 0 ? (
                            activos.map((a, i) => {
                                const ultimo = i === activos.length - 1;
                                return (
                                    <React.Fragment key={a.chave}>
                                        {i > 0 && <ChevronRight className="size-3.5 shrink-0 text-zinc-300 dark:text-zinc-600" aria-hidden />}
                                        {i === 0 && a.icone && <Icon icon={a.icone} className="shrink-0 text-lg text-portal dark:text-portal-claro" />}
                                        <span className={cn("truncate", ultimo ? "font-semibold text-zinc-900 dark:text-zinc-50" : "text-zinc-500 dark:text-zinc-400", !ultimo && "hidden lg:inline")}>{a.rotulo}</span>
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <span className="truncate font-semibold text-zinc-900 dark:text-zinc-50">{marca.nome}</span>
                        )}
                    </nav>

                    <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
                        <button
                            type="button"
                            data-tour="pesquisa"
                            onClick={() => setPaleta(true)}
                            className="hidden h-9 items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 pl-2.5 pr-1.5 text-sm text-zinc-500 transition-colors hover:border-zinc-300 hover:bg-white hover:text-zinc-700 lg:flex dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-400 dark:hover:bg-zinc-800"
                        >
                            <Search className="size-4" />
                            <span className="w-36 text-left">Pesquisar…</span>
                            <kbd className="rounded border border-zinc-200 bg-white px-1.5 text-[11px] font-medium text-zinc-400 dark:border-zinc-600 dark:bg-zinc-900">⌘K</kbd>
                        </button>
                        <BotaoIcone rotulo="Pesquisar (⌘K)" tamanho="lg" className="lg:hidden" data-tour="pesquisa-telemovel" onClick={() => setPaleta(true)}>
                            <Search className="size-5" />
                        </BotaoIcone>

                        {accoesCabecalho}

                        {notificacoes && (
                            <Link
                                href={notificacoes.href}
                                data-tour="notificacoes"
                                aria-label={notificacoes.total ? `Notificações (${notificacoes.total})` : "Notificações"}
                                title="Notificações"
                                className="relative inline-flex size-10 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
                            >
                                <Bell className="size-5" />
                                {!!notificacoes.total && notificacoes.total !== "0" && (
                                    <span className="absolute right-1 top-1 flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-[18px] text-white ring-2 ring-white dark:ring-zinc-900">
                                        {Number(notificacoes.total) > 99 ? "99+" : notificacoes.total}
                                    </span>
                                )}
                            </Link>
                        )}

                        {tema && <BotaoTema {...tema} className="hidden sm:inline-flex" />}

                        {utilizador && (
                            <Menu
                                largura={260}
                                gatilho={
                                    <button type="button" aria-label="A tua conta" data-tour="conta" className="ml-0.5 flex shrink-0 items-center gap-2.5 rounded-full p-0.5 transition-colors hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/40 dark:hover:bg-zinc-800 xl:pl-3">
                                        <span className="hidden max-w-[160px] truncate text-sm font-medium text-zinc-800 dark:text-zinc-200 xl:block">{utilizador.nome}</span>
                                        <Avatar src={utilizador.foto} nome={utilizador.nome} tamanho="md">
                                            <Icon icon="mdi:account" />
                                        </Avatar>
                                    </button>
                                }
                                itens={[
                                    {
                                        chave: "quem",
                                        rotulo: (
                                            <span className="min-w-0">
                                                <span className="block truncate font-semibold text-zinc-900 dark:text-zinc-100">{utilizador.nome}</span>
                                                {utilizador.email && <span className="block truncate text-xs text-zinc-500">{utilizador.email}</span>}
                                            </span>
                                        ),
                                    },
                                    ...(tema ? itensDoTema(tema, true) : []),
                                    ...menuConta.map((m, i) => (i === 0 ? { ...m, separadorAntes: true } : m)),
                                ]}
                            />
                        )}
                    </div>
                </header>

                {avisos && <div className="print:hidden">{avisos}</div>}

                <main
                    data-miolo-painel
                    data-sem-margens={semMargens || undefined}
                    className={cn(
                        semMargens
                            ? "min-h-[calc(100dvh-4rem)] pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0"
                            : "mx-auto w-full max-w-[1600px] px-3 py-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-6 md:pb-8 lg:px-8",
                        "print:p-0",
                    )}
                >
                    {children}
                </main>
            </div>

            {/* A gaveta do telemóvel */}
            {saidaGaveta.montada && (
                <div className={cn("fixed inset-0 z-50 lg:hidden", saidaGaveta.aSair && "pointer-events-none")} role="dialog" aria-modal="true" aria-label="Menu" aria-hidden={saidaGaveta.aSair || undefined}>
                    <div className={cn("absolute inset-0 bg-zinc-950/50 backdrop-blur-[2px]", saidaGaveta.aSair ? "animate-out fade-out-0 fill-mode-forwards duration-150" : "animate-in fade-in-0")} onClick={() => setGaveta(false)} aria-hidden />
                    <aside className={cn("relative flex h-full w-[300px] max-w-[85vw] flex-col pt-[env(safe-area-inset-top)] shadow-2xl", saidaGaveta.aSair ? "animate-out slide-out-to-left fill-mode-forwards duration-150" : "animate-in slide-in-from-left duration-200", fundoMenu)}>
                        <div className="flex h-16 shrink-0 items-center gap-2 pl-4 pr-2">
                            <div className="min-w-0 flex-1">{cabecaMarca(false)}</div>
                            <BotaoIcone rotulo="Fechar o menu" tamanho="lg" onClick={() => setGaveta(false)}>
                                <X className="size-5" />
                            </BotaoIcone>
                        </div>
                        {antesDoMenu && <div className="shrink-0 px-3 pb-3">{antesDoMenu}</div>}
                        <MenuLateral itens={itens} activos={activos} chave={`painel-${chave}`} onNavegar={() => setGaveta(false)} nome="Menu" pesquisavel={false} />
                        {tema && (
                            <div className="shrink-0 border-t border-zinc-100 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] dark:border-white/10">
                                <TemaSegmentado escolhido={tema.escolhido} mudar={tema.mudar} />
                            </div>
                        )}
                    </aside>
                </div>
            )}

            <BarraInferior destinos={destinos} />
            <PaletaComandos aberta={paleta} onFechar={() => setPaleta(false)} entradas={entradas} accoes={accoesPaleta} />
        </div>
    );
}
